import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { LatLngTuple } from 'leaflet';
import { CoKitchenPolygons } from 'models/cokitchen.model';
import PolygonModal, { PolygonModalType } from 'components/logistics/polygon-modal';
import { PolygonItemType } from 'components/logistics/polygon-card/polygon-item';
import { apiService } from 'utils/api';
import BackButton from 'components/base/back-button';
import PageHeaderText from 'components/base/page-header-text';
import Map from 'components/logistics/map';
import PolygonCard from 'components/logistics/polygon-card';
import DeleteModal from 'components/base/delete-modal';
import { AddIcon, DeleteIcon, EditAltIcon } from 'cokitchen-icons';
import useDashboardContext from 'contexts/dashboard.context';
import { useLocation } from 'react-router-dom';

const baseUrl = process.env.REACT_APP_BRAND_MGT_API_URL;

const LocationSettings = () => {
  const { state } = useLocation();
  const { polygonId, type: redirectType } = state || {};

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showPolygonModal, setShowPolygonModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [type, setType] = useState<PolygonModalType>('create');
  const { activeCokitchen } = useDashboardContext();
  const [activePolygon, setActivePolygon] = useState<CoKitchenPolygons | null>(null);
  useEffect(() => {
    if (!polygonId) return;

    setActivePolygon(activeCokitchen.cokitchen_polygons?.find((p) => p.id === polygonId) ?? null);
    setType('edit');
    if (redirectType === 'edit') {
      setShowPolygonModal(true);
    } else if (redirectType === 'delete') {
      setShowDeleteModal(true);
    }
  }, [polygonId]);
  const togglePolygonModal = () => {
    setShowPolygonModal(!showPolygonModal);
  };
  const addPolygon = () => {
    setType('create');
    togglePolygon();
  };

  const togglePolygon = () => {
    togglePolygonModal();
  };

  const editPolygon = (polygon?: CoKitchenPolygons) => {
    if (polygon) setActivePolygon(polygon);
    setType('edit');
    togglePolygon();
  };

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const POLYGON_MENU: PolygonItemType[] = [
    {
      name: 'Edit Polygon',
      icon: <EditAltIcon />,
      onClick: editPolygon
    },
    {
      name: 'Delete Polygon',
      icon: <DeleteIcon className='text-red-5' />,
      onClick: toggleDeleteModal,
      className: 'text-red-5'
    }
  ];

  const deletePolygon = async () => {
    setDeleteLoading(true);

    try {
      const res = await apiService.delete(`marketing/cokitchen-polygon/${activePolygon?.id}`, {
        baseUrl
      });
      if (res?.data) {
        toast(
          <ToastMessage type='success' message={`${activePolygon?.name} deleted successfully`} />
        );
        toggleDeleteModal();
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <BackButton />

      <PageHeaderText
        title='Manage FoodCourts delivery locations'
        className='m6-4'
        description='Create new delivery polygons and expand the reach of our current locations'>
        <Button
          onClick={addPolygon}
          variant='link'
          color='primary'
          className='bg-primary text-red px-6 py-2 text-sm font-semibold'>
          <AddIcon className='w-5 h-5' />
          Add polygon
        </Button>
      </PageHeaderText>
      {activeCokitchen?.cokitchen_polygons === null ? (
        <div className='grid place-content-center h-[40vh] font-medium'>
          No Polygons To Show For {activeCokitchen?.name}
        </div>
      ) : (
        <div className='flex flex-col lg:flex-row gap-6'>
          <Map withMarker onPolygonClick={editPolygon} cokitchen={activeCokitchen} />
          <div className='self-end'>
            <PolygonCard
              list={POLYGON_MENU}
              setActivePolygon={setActivePolygon}
              polygons={activeCokitchen?.cokitchen_polygons}
            />
          </div>
        </div>
      )}
      <PolygonModal
        show={showPolygonModal}
        onClose={() => {
          togglePolygonModal();
          setActivePolygon(null);
        }}
        positions={activePolygon?.polygon as LatLngTuple[]}
        data={{
          name: activePolygon?.name ?? '',
          delivery_fee: activePolygon?.delivery_fee ?? '',
          delivery_service_charge: activePolygon?.delivery_service_charge ?? '',
          cokitchen_polygon_id: activePolygon?.id ?? ''
        }}
        type={type}
      />
      {showDeleteModal && (
        <DeleteModal
          show
          onClose={toggleDeleteModal}
          type='delete'
          description={`This will permanently delete ${activePolygon?.name}`}
          handleDelete={deletePolygon}
          loading={deleteLoading}
          title='Delete Polygon'
          emoji='!'
          color='red'
        />
      )}
    </div>
  );
};

export default LocationSettings;
